/**
 * What the wallet page shows (2026-10-25): every wallet the driver has, one
 * per currency, and the currency a top-up goes into.
 *
 *   - wallets: from the Api's list; an older Api (one wallet) and a driver
 *     with no wallet yet still show one card, in the legacy currency.
 *   - topUpCurrencies: the Api's list (USD, plus CAD for a driver who
 *     charges in Canada). A link from the screen before a CAD start
 *     (?currency=CAD) adds CAD for this visit: that driver is at a CAD
 *     charger now. The top-up route accepts either currency.
 *   - topUpCurrency: the one picked on the toggle, else the linked one, else
 *     the first wallet's. It is the wallet the top-up goes to.
 *
 * Every wallet stays on screen whatever the toggle says (Warren, 2026-10-25:
 * "selecting USD makes CAD disappear"). The toggle only picks the target:
 * the linked currency stays in the set after the driver picks another one,
 * so a CAD wallet a CAD charger linked to (empty until its first top-up)
 * does not vanish when the toggle moves to USD.
 *
 * Pure, so node tests run it directly.
 */
import type { CashWalletPayload } from '@/lib/api';

const KNOWN = ['USD', 'CAD'] as const;
export type WalletPageCurrency = (typeof KNOWN)[number];
const known = (v: unknown): WalletPageCurrency | null => {
  const c = typeof v === 'string' ? v.trim().toUpperCase() : '';
  return (KNOWN as readonly string[]).includes(c) ? (c as WalletPageCurrency) : null;
};

export interface WalletPageState {
  wallets: Array<{ currency: string; balanceCents: number }>;
  topUpCurrencies: WalletPageCurrency[];
  /** The wallet the top-up goes to (the toggle's choice). */
  topUpCurrency: WalletPageCurrency;
  /** More than one currency is in play: show the wallet labels and the
   *  currency picker. A USD-only driver sees the page as it always was. */
  multiCurrency: boolean;
}

/**
 * @param linked the currency the page was linked with (?currency=), kept
 *   for the whole visit
 * @param picked the currency the driver picked on the toggle, if any
 */
export function walletPageState(
  wallet: CashWalletPayload,
  linked: string | null,
  picked: string | null = null,
): WalletPageState {
  const legacy = known(wallet.currency) ?? 'USD';
  const wallets = wallet.wallets && wallet.wallets.length > 0
    ? wallet.wallets
    : [{ currency: legacy, balanceCents: wallet.balanceCents }];
  const listed = (wallet.topUpCurrencies ?? [legacy]).map(known).filter((c): c is WalletPageCurrency => c !== null);
  const fromLink = known(linked);
  const choice = known(picked);
  const currencies = new Set<WalletPageCurrency>(listed.length > 0 ? listed : [legacy]);
  if (fromLink) currencies.add(fromLink);
  for (const w of wallets) { const c = known(w.currency); if (c) currencies.add(c); }
  const topUpCurrencies = KNOWN.filter((c) => currencies.has(c));
  const first = known(wallets[0]?.currency) ?? legacy;
  const topUpCurrency = choice && currencies.has(choice) ? choice
    : fromLink && currencies.has(fromLink) ? fromLink
    : currencies.has(first) ? first
    : topUpCurrencies[0] ?? 'USD';
  // A currency the driver can top up but holds no wallet in yet shows as an
  // empty wallet, so "your CAD wallet" exists on screen before the first top-up.
  const shown = [...wallets];
  for (const c of topUpCurrencies) if (!shown.some((w) => known(w.currency) === c)) shown.push({ currency: c, balanceCents: 0 });
  shown.sort((a, b) => KNOWN.indexOf(known(a.currency) ?? 'USD') - KNOWN.indexOf(known(b.currency) ?? 'USD'));
  return { wallets: shown, topUpCurrencies, topUpCurrency, multiCurrency: topUpCurrencies.length > 1 };
}
